import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Spinner,
} from '@helios/ui';
import {
  companySetupClient,
  type InvitationAuthority,
  type SetupSummary,
} from '@/services/companySetupClient';
import { SettingsField, SettingsSection, settingsControlClass } from './settingsChrome';

const emptyDraft = {
  legalName: '',
  slug: '',
  ownerName: '',
  ownerEmail: '',
  licensedSeats: 1,
  ownerVerificationNote: '',
  ownerAuthorityConfirmed: false,
};
const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'The invitation request could not be completed.';

/** The containing tenant workspace remounts this panel whenever the staff scope changes. */
export function SubscriberInvitationsPanel({
  onOperatorChange,
}: {
  onOperatorChange?: (allowed: boolean) => void;
}) {
  const [authority, setAuthority] = useState<InvitationAuthority | null>(null);
  const [rows, setRows] = useState<SetupSummary[]>([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const mounted = useRef(true);
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setAuthority(null);
    setRows([]);
    onOperatorChange?.(false);
    try {
      const scope = await companySetupClient.invitationAuthority();
      if (!mounted.current || request !== generation.current) return;
      const setups = scope.platformOperator
        ? await companySetupClient.list({ page, q: q || undefined })
        : [];
      if (!mounted.current || request !== generation.current) return;
      setAuthority(scope);
      setRows(setups);
      onOperatorChange?.(scope.platformOperator);
      setError(null);
    } catch (caught) {
      if (mounted.current && request === generation.current) setError(errorText(caught));
    } finally {
      if (mounted.current && request === generation.current) setLoading(false);
    }
  }, [page, q, onOperatorChange]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, [refresh]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !authority?.emailAvailable || !authority.deliveryFingerprint) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await companySetupClient.create({
        ...draft,
        delivery: 'EMAIL',
        deliveryFingerprint: authority.deliveryFingerprint,
      });
      if (!mounted.current) return;
      setNotice(result.detail ?? 'Invitation created. Review its delivery status below.');
      setDraft(emptyDraft);
      setOpen(false);
      if (page === 1 && !q) await refresh();
      else {
        setPage(1);
        setQ('');
        setSearch('');
      }
    } catch (caught) {
      if (mounted.current) {
        await refresh();
        if (mounted.current) setError(errorText(caught));
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const act = async (row: SetupSummary, revoke: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (revoke) {
        await companySetupClient.revokeInvitation(row.id, row.version);
        if (mounted.current) setNotice(`The invitation for ${row.ownerEmail} is revoked.`);
      } else {
        const result = await companySetupClient.reissue(
          row.id,
          row.version,
          'EMAIL',
          authority?.deliveryFingerprint ?? undefined,
        );
        if (mounted.current) setNotice(result.detail ?? 'Replacement invitation requested.');
      }
      if (mounted.current) await refresh();
    } catch (caught) {
      if (mounted.current) {
        await refresh();
        if (mounted.current) setError(errorText(caught));
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <SettingsSection
      title="Subscriber invitations"
      description="Invite a verified company owner into a separate workspace. Invitations expire after 72 hours and are accepted once before onboarding can begin."
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={
            loading ||
            busy ||
            !authority?.platformOperator ||
            !authority.emailAvailable ||
            !authority.deliveryFingerprint
          }
          onClick={() => setOpen(true)}
        >
          Invite subscriber
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={loading || busy}
          onClick={() => void refresh()}
        >
          Refresh invitations
        </Button>
        {loading ? <Spinner className="h-4 w-4" aria-label="Loading invitations" /> : null}
      </div>
      {authority ? (
        <p className="text-muted-foreground mt-2 text-xs">
          {authority.platformOperator
            ? authority.emailAvailable
              ? `Platform operator · Sender: ${authority.sender ?? 'configured platform email'}`
              : `Platform operator · Email setup required for ${authority.requiredSender ?? 'the HELIOS platform sender'}. Configure this mailbox for platform invitations and the public onboarding URL.`
            : 'Workspace administrator · Subscriber provisioning requires an explicitly authorized cross-tenant platform operator.'}
        </p>
      ) : null}
      {authority?.platformOperator && !authority.emailAvailable ? (
        <Link className="mt-1 block text-xs underline" to="/settings/integrations">
          Open integrations and email setup
        </Link>
      ) : null}
      {error ? (
        <p role="alert" className="text-critical mt-2 text-xs">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="mt-2 text-xs">
          {notice}
        </p>
      ) : null}
      {authority?.platformOperator ? (
        <div className="mt-3 space-y-2">
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setQ(search.trim());
            }}
          >
            <SettingsField label="Search invitations">
              <Input
                maxLength={120}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Company, workspace address, or owner email"
              />
            </SettingsField>
            <Button type="submit" size="sm" variant="outline" disabled={busy || loading}>
              Search
            </Button>
          </form>
          {!loading && rows.length === 0 ? (
            <p className="text-muted-foreground text-xs">No company invitations yet.</p>
          ) : null}
          {rows.map((row) => (
            <div
              key={row.id}
              className="border-border flex flex-wrap items-center justify-between gap-2 rounded-md border p-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{row.name}</p>
                <p className="text-muted-foreground break-all text-xs">
                  {row.ownerName} · {row.ownerEmail}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  <Badge variant="outline">
                    {row.invitationState ?? (row.invitationAccepted ? 'ACCEPTED' : 'PENDING')}
                  </Badge>
                  {!row.invitationAccepted ? (
                    <Badge variant="outline">
                      {row.deliveryState === 'ACCEPTED'
                        ? 'Email accepted by provider'
                        : row.deliveryState === 'FAILED'
                          ? 'Email failed'
                          : 'Email unconfirmed'}
                    </Badge>
                  ) : null}
                </div>
                {row.invitationExpiresAt && !row.invitationAccepted ? (
                  <p className="text-muted-foreground mt-1 text-xs">
                    Expires {new Date(row.invitationExpiresAt).toLocaleString()}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-1.5">
                <Link className="text-xs underline" to={`/company-setup/${row.id}`}>
                  Open onboarding
                </Link>
                {!row.invitationAccepted && row.status === 'DRAFT' ? (
                  <>
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={
                        busy ||
                        loading ||
                        !authority.emailAvailable ||
                        !authority.deliveryFingerprint
                      }
                      onClick={() => void act(row, false)}
                    >
                      Resend invitation
                    </Button>
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={busy || loading || row.invitationState === 'REVOKED'}
                      onClick={() => void act(row, true)}
                    >
                      Revoke invitation
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={page === 1 || loading || busy}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous invitations
            </Button>
            <span className="text-xs">Page {page} · up to 50 per page</span>
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={rows.length < 50 || loading || busy}
              onClick={() => setPage((current) => current + 1)}
            >
              Next invitations
            </Button>
          </div>
        </div>
      ) : null}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite subscriber</DialogTitle>
            <DialogDescription>
              Email an exclusive activation link to the verified company owner. This creates a
              pending workspace and seat allowance; it does not create a billing charge.
              <span className="mt-1 block font-medium">
                From: {authority?.sender ?? 'configured platform email'}. Review this sender before
                submitting.
              </span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => void submit(event)} className="grid gap-3">
            <fieldset disabled={busy} className="grid gap-3">
              <SettingsField label="Company name">
                <Input
                  required
                  minLength={2}
                  maxLength={160}
                  value={draft.legalName}
                  onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Workspace address">
                <Input
                  required
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  minLength={3}
                  maxLength={63}
                  value={draft.slug}
                  onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Owner name">
                <Input
                  required
                  minLength={2}
                  maxLength={120}
                  value={draft.ownerName}
                  onChange={(e) => setDraft({ ...draft, ownerName: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Owner email">
                <Input
                  required
                  type="email"
                  value={draft.ownerEmail}
                  onChange={(e) => setDraft({ ...draft, ownerEmail: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Licensed users, including the owner">
                <Input
                  required
                  type="number"
                  min={1}
                  max={500}
                  value={draft.licensedSeats}
                  onChange={(e) => setDraft({ ...draft, licensedSeats: Number(e.target.value) })}
                />
              </SettingsField>
              <SettingsField label="How you verified this owner's authority">
                <textarea
                  required
                  minLength={15}
                  maxLength={1000}
                  className={settingsControlClass}
                  value={draft.ownerVerificationNote}
                  onChange={(e) => setDraft({ ...draft, ownerVerificationNote: e.target.value })}
                />
              </SettingsField>
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  required
                  checked={draft.ownerAuthorityConfirmed}
                  onChange={(e) =>
                    setDraft({ ...draft, ownerAuthorityConfirmed: e.target.checked })
                  }
                />
                I independently verified this owner's authority and reviewed the seat allowance.
              </label>
            </fieldset>
            {error ? (
              <p role="alert" className="text-critical text-xs">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  busy ||
                  loading ||
                  !authority?.emailAvailable ||
                  !authority.deliveryFingerprint ||
                  !draft.ownerAuthorityConfirmed
                }
              >
                {busy ? 'Sending invitation…' : 'Email invitation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
